import ECSFullSheetLayer from 'components/editor/ECSFullSheetLayer';
import ECSKeyboardEvents from 'components/editor/ECSKeyboardEvents';
import ECSSheetEvents from 'components/editor/ECSSheetEvents';
import ECSSheetMarkup from 'components/editor/ECSSheetMarkup';
import ECSSheetOverlay from 'components/editor/ECSSheetOverlay';
import ECSSpaceLayer from 'components/editor/ECSSpaceLayer';
import AppStoreContext from 'contexts/AppStoreContext';
import ECSEditorContext from 'contexts/ECSEditorContext';
import Canvas from 'editor/components/Canvas';
import CarnivoreSheet from 'editor/components/CarnivoreSheet';
import ToolOverlay from 'editor/components/ToolOverlay';
import { OrderedMap } from 'immutable';
import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Markup as MarkupRecord } from 'records/Markup';
import { Sheet as SheetRecord } from 'records/Sheet';
import { getPageUrl } from 'services/pageService';
import { loadImage } from 'utils/image';

type Props = PropsWithChildren<{
  documentid: string;
  pageid: string;
  sheet?: SheetRecord;
  width?: number;
  height?: number;
  withMarkups?: boolean;
  isInteractive?: boolean;
}>;

// This component creates and manages the Carnivore "Sheet" component.
const Sheet: FC<Props> = (props: Props) => {
  const {
    documentid,
    pageid,
    sheet,
    width,
    height,
    withMarkups,
    isInteractive,
  } = props;
  const appStore = useContext(AppStoreContext);
  const { doc, carnCtx } = useContext(ECSEditorContext);
  const [sheetComp, setSheetComp] = useState<CarnivoreSheet | null>(null);
  const [canvasComp, setCanvasComp] = useState<Canvas | null>(null);
  const { markups } = appStore.state;

  const isInitialMount = useRef(true);
  const sheetCompRef = useRef({} as CarnivoreSheet);

  useEffect(() => {
    async function createSheetEntity() {
      if (sheet) {
        await Promise.all(
          sheet.versions
            .map((v) => appStore.actions.getSheetMarkups(v.pageid))
            .toList()
        );
      } else {
        await appStore.actions.getSheetMarkups(pageid);
      }

      const _canvasComp = new Canvas();
      const pageUrl = await getPageUrl(documentid, pageid);
      const imgCanvas = await loadImage(pageUrl);

      if (
        width &&
        height &&
        (imgCanvas.width !== width || imgCanvas.height !== height)
      ) {
        // Handle cases where the size of the sheet image does not match the given width/height.
        // This usually happens when the original image size is 2x the actual sheet dimensions.
        // As used to be the case with all rendered images.
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = width;
        tempCanvas.height = height;

        tempCtx?.transform(
          width / imgCanvas.width,
          0,
          0,
          height / imgCanvas.height,
          0,
          0
        );

        tempCtx?.drawImage(imgCanvas, 0, 0);

        _canvasComp.canvas = tempCanvas;
      } else {
        _canvasComp.canvas = imgCanvas;
      }

      /* ----------------------------------- */

      const carnSheet = carnCtx.ctx.createSheet({
        id: `sheet-${pageid}`,
        width: _canvasComp.canvas.width,
        height: _canvasComp.canvas.height,
      });

      const _sheetComp = new CarnivoreSheet(carnSheet);
      // when multiple sheets are loaded, the doc should only reference the current one so purge previous
      doc.doc.clearSheets();
      doc.doc.addSheet(carnSheet.id);
      doc.doc.zoomFullOnSheet(carnSheet.id);

      setSheetComp(_sheetComp);
      // store the current sheet for cleanup on unmount
      sheetCompRef.current = _sheetComp;
      setCanvasComp(_canvasComp);
    }

    if (isInitialMount.current) {
      // always create the first sheet entity on first mount
      isInitialMount.current = false;
      createSheetEntity();
    } else {
      // if this is the next sheet in a batch, clear out the old one before creating and loading it
      if (sheetComp) {
        sheetComp.sheet.destroy();
      }

      createSheetEntity();
    }
  }, []);

  useEffect(
    () => () => {
      // on unmount, destroy whichever sheet component was last loaded
      if (sheetCompRef.current) {
        const { sheet } = sheetCompRef.current;
        sheet?.destroy();
      }
    },
    []
  );

  /* ----------------------------------------- */

  const sheetMarkups = sheet
    ? ([] as MarkupRecord[]).concat(
        ...sheet.versions
          .map((v) => {
            return markups
              .get(v.pageid, OrderedMap<string, MarkupRecord>())
              .valueSeq()
              .toArray();
          })
          .toArray()
      )
    : markups
        .get(pageid, OrderedMap<string, MarkupRecord>())
        .valueSeq()
        .toArray();

  const additionalComponents = [];
  if (isInteractive) {
    additionalComponents.push(new ToolOverlay());
  }

  return sheetComp && canvasComp ? (
    <>
      <ECSFullSheetLayer
        sheetComp={sheetComp}
        canvasComp={canvasComp}
        index={0}
      />
      <ECSSpaceLayer
        sheetComp={sheetComp}
        index={1}
        width={canvasComp.canvas.width}
        height={canvasComp.canvas.height}
        documentid={documentid}
        pageid={pageid}
      />
      {withMarkups && (
        <ECSSheetOverlay
          key={pageid}
          sheetComp={sheetComp}
          id="markup-overlay"
          index={0}
          additionalComponents={additionalComponents}
        >
          {sheetMarkups &&
            sheetMarkups.map((markup) => {
              return (
                <ECSSheetMarkup
                  key={markup.markupid}
                  markup={markup}
                  sheetComp={sheetComp}
                />
              );
            })}
        </ECSSheetOverlay>
      )}
      <ECSKeyboardEvents />
      <ECSSheetEvents sheetComp={sheetComp} pageid={pageid} />
    </>
  ) : null;
};

export default Sheet;
